import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, formatCOP } from "@/lib/api";
import { LogOut, Ticket, Plus, DollarSign, Hash, Loader2, ShoppingBag, MessageCircle, Wallet } from "lucide-react";
import FichaMap from "@/components/FichaMap";
import SellerReservations from "@/components/SellerReservations";
import { openReceipt } from "@/lib/receipt";

const PAYMENTS = ["Efectivo", "Nequi", "Daviplata", "Transferencia", "Tarjeta"];

export default function SellerDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState({ sales: [], count: 0, total_amount: 0, total_commission: 0, commission_per_ficha: 10000 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ficha_number: "", buyer_name: "", buyer_phone: "", payment_method: "Efectivo" });
  const [saving, setSaving] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [resKey, setResKey] = useState(0);
  const [reserving, setReserving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/sales/mine");
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ficha_number.trim() || !form.buyer_name.trim()) {
      toast.error("Ingresa el número de ficha y el nombre del comprador");
      return;
    }
    setSaving(true);
    try {
      const { data: sale } = await api.post("/sales", form);
      toast.success(`Ficha #${String(form.ficha_number).padStart(4, "0")} registrada`, {
        description: "Envía el comprobante al comprador",
        action: { label: "Comprobante WhatsApp", onClick: () => openReceipt(sale) },
        duration: 12000,
      });
      setForm({ ficha_number: "", buyer_name: "", buyer_phone: "", payment_method: "Efectivo" });
      setMapKey((k) => k + 1);
      setResKey((k) => k + 1);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const reserve = async () => {
    if (!form.ficha_number.trim()) {
      toast.error("Ingresa el número de ficha a reservar");
      return;
    }
    setReserving(true);
    try {
      await api.post("/reservations", { ficha_number: form.ficha_number, buyer_name: form.buyer_name, buyer_phone: form.buyer_phone, minutes: 30 });
      toast.success(`Ficha #${String(form.ficha_number).padStart(4, "0")} reservada por 30 min`);
      setForm({ ficha_number: "", buyer_name: "", buyer_phone: "", payment_method: "Efectivo" });
      setMapKey((k) => k + 1);
      setResKey((k) => k + 1);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setReserving(false);
    }
  };

  const refreshAll = () => {
    setMapKey((k) => k + 1);
    setResKey((k) => k + 1);
    load();
  };

  const inputCls =
    "w-full bg-[#051A0F] border border-[#F0B91B]/20 rounded-xl px-4 py-3 text-[#FDFBF7] font-outfit placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#F0B91B] transition-colors";

  return (
    <div className="min-h-screen bg-[#051A0F] grain-overlay" data-testid="seller-dashboard">
      {/* Top bar */}
      <header className="border-b border-[#F0B91B]/15 bg-[#0A2818]/60 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0B91B] to-[#E5A200] flex items-center justify-center">
              <Ticket className="w-4 h-4 text-[#051A0F]" />
            </div>
            <div>
              <div className="font-bebas text-xl text-[#F0B91B] tracking-widest leading-none">BETMAR</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#FDFBF7]/50 font-outfit">Vendedor</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#FDFBF7]/80 font-outfit hidden sm:inline" data-testid="seller-name">
              Hola, <strong className="text-[#FDFBF7]">{user?.name}</strong>
            </span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-[#FDFBF7]/70 hover:text-[#E63946] font-outfit transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-[#F0B91B]/25 bg-[#0A2818]/70 p-6 flex items-center gap-4" data-testid="summary-count">
            <div className="w-12 h-12 rounded-xl bg-[#F0B91B]/15 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-[#F0B91B]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#FDFBF7]/50 font-outfit">Fichas vendidas</div>
              <div className="font-bebas text-4xl text-[#FDFBF7] tracking-wide leading-none mt-1">{data.count}</div>
            </div>
          </div>
          <div className="rounded-2xl border border-[#7FD1AE]/25 bg-[#0A2818]/70 p-6 flex items-center gap-4" data-testid="summary-amount">
            <div className="w-12 h-12 rounded-xl bg-[#7FD1AE]/15 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-[#7FD1AE]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#FDFBF7]/50 font-outfit">Total recaudado</div>
              <div className="font-bebas text-4xl gold-shimmer tracking-wide leading-none mt-1">{formatCOP(data.total_amount)}</div>
            </div>
          </div>
          <div className="rounded-2xl border-2 border-[#F0B91B]/50 bg-[#0A2818]/80 p-6 flex items-center gap-4 shadow-[0_10px_30px_-15px_rgba(240,185,27,0.5)]" data-testid="summary-commission">
            <div className="w-12 h-12 rounded-xl bg-[#F0B91B]/20 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-[#F0B91B]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#F0B91B] font-outfit font-semibold">Mi comisión</div>
              <div className="font-bebas text-4xl gold-shimmer tracking-wide leading-none mt-1" data-testid="commission-total">{formatCOP(data.total_commission || 0)}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/50 font-outfit mt-1">
                {formatCOP(data.commission_per_ficha || 10000)} × ficha
              </div>
            </div>
          </div>
        </div>

        {/* Register form */}
        <section id="seller-register-anchor" className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 p-6 md:p-8">
          <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#F0B91B]" /> Registrar venta de ficha
          </h2>
          <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sale-form">
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDFBF7]/40" />
              <input
                value={form.ficha_number}
                onChange={(e) => setForm({ ...form, ficha_number: e.target.value })}
                placeholder="Número de ficha (0000–9999)"
                className={inputCls + " pl-10"}
                data-testid="sale-ficha"
                inputMode="numeric"
                maxLength={4}
              />
            </div>
            <input
              value={form.buyer_name}
              onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
              placeholder="Nombre del comprador"
              className={inputCls}
              data-testid="sale-buyer"
            />
            <input
              value={form.buyer_phone}
              onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })}
              placeholder="Teléfono (opcional)"
              className={inputCls}
              data-testid="sale-phone"
            />
            <select
              value={form.payment_method}
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              className={inputCls}
              data-testid="sale-payment"
            >
              {PAYMENTS.map((p) => (
                <option key={p} value={p} className="bg-[#0A2818]">{p}</option>
              ))}
            </select>
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="submit"
                disabled={saving || reserving}
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] disabled:opacity-60 text-[#051A0F] py-3.5 rounded-xl font-bebas text-xl tracking-wider transition-all hover:-translate-y-0.5"
                data-testid="sale-submit"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : `Registrar venta · ${formatCOP(50000)}`}
              </button>
              <button
                type="button"
                onClick={reserve}
                disabled={saving || reserving}
                className="inline-flex items-center justify-center gap-2 bg-[#0A2818] border border-[#F0B91B]/50 hover:border-[#F0B91B] disabled:opacity-60 text-[#F0B91B] py-3.5 rounded-xl font-bebas text-lg tracking-wider transition-all"
                data-testid="reserve-submit"
              >
                {reserving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reservar 30 min"}
              </button>
            </div>
          </form>
        </section>

        {/* Active reservations */}
        <SellerReservations refreshKey={resKey} onChange={refreshAll} />

        {/* Ficha map */}
        <FichaMap
          refreshKey={mapKey}
          onPick={(num) => {
            setForm((f) => ({ ...f, ficha_number: num }));
            // Scroll to form and focus the buyer name input
            document.getElementById("seller-register-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
            setTimeout(() => {
              const el = document.querySelector('[data-testid="sale-buyer"]');
              if (el && typeof el.focus === "function") el.focus();
            }, 550);
          }}
        />

        {/* Sales table */}
        <section className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 overflow-hidden">
          <div className="px-6 py-4 border-b border-[#F0B91B]/15">
            <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider">Mis ventas</h2>
          </div>
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#F0B91B] animate-spin" /></div>
          ) : data.sales.length === 0 ? (
            <div className="p-10 text-center text-[#FDFBF7]/50 font-outfit" data-testid="no-sales">
              Aún no has registrado ventas. ¡Registra tu primera ficha!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sales-table">
                <thead>
                  <tr className="text-left text-[#F0B91B] font-outfit uppercase text-[10px] tracking-widest border-b border-[#F0B91B]/10">
                    <th className="px-6 py-3">Ficha</th>
                    <th className="px-6 py-3">Comprador</th>
                    <th className="px-6 py-3">Teléfono</th>
                    <th className="px-6 py-3">Método</th>
                    <th className="px-6 py-3 text-right">Monto</th>
                    <th className="px-6 py-3">Fecha</th>
                    <th className="px-6 py-3 text-center">Comprobante</th>
                  </tr>
                </thead>
                <tbody className="font-outfit text-[#FDFBF7]/85">
                  {data.sales.map((s) => (
                    <tr key={s.id} className="border-b border-[#F0B91B]/5 hover:bg-[#F0B91B]/5 transition-colors">
                      <td className="px-6 py-3 font-bebas text-lg tracking-wider text-[#F0B91B]">#{s.ficha_number}</td>
                      <td className="px-6 py-3">{s.buyer_name}</td>
                      <td className="px-6 py-3 text-[#FDFBF7]/60">{s.buyer_phone || "—"}</td>
                      <td className="px-6 py-3">{s.payment_method}</td>
                      <td className="px-6 py-3 text-right">{formatCOP(s.amount)}</td>
                      <td className="px-6 py-3 text-[#FDFBF7]/60">{new Date(s.created_at).toLocaleDateString("es-CO")}</td>
                      <td className="px-6 py-3 text-center">
                        <button onClick={() => openReceipt(s)} title="Enviar comprobante por WhatsApp" className="inline-flex items-center gap-1.5 text-xs text-[#25D366] hover:bg-[#25D366]/10 px-3 py-1.5 rounded-lg transition-colors" data-testid={`receipt-${s.ficha_number}`}>
                          <MessageCircle className="w-4 h-4" /> Enviar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
